import * as Api from '@ViewModels';
import { findPlatformCapabilities } from './platformCapabilities';
import { IPlatformCustomizationDraft, findPlatformCustomization } from './platformCustomizations';

export type PlatformValidationField = 'content' | 'attachments' | 'platform';

/**
 * When the API applies the rule behind an error.
 *
 * `save` rules run on every create and update — per-platform content length, and every media ceiling
 * — so a post carrying one cannot be stored at all, draft or not. `publish` rules run only once a post
 * is ready to publish: the API gates them on the due date, and a post without one is a draft. The media
 * *requirement* is a publish rule for exactly that reason, which is what keeps an empty draft saveable.
 * The composer gates its buttons on this rather than on the error count, so it never blocks a save the
 * API would have accepted.
 */
export type PlatformValidationScope = 'save' | 'publish';

/**
 * One problem with a draft. These are the composer's own checks, run against the limits the API
 * reports — there is no endpoint that returns them, so the shape is ours rather than a server model.
 */
export interface IPlatformValidationError {
	/** Absent for a problem with the post as a whole, such as its shared media pool. */
	platform?: Api.SocialMediaType;
	field: PlatformValidationField;
	scope: PlatformValidationScope;
	message: string;
}

export interface IValidateSocialPostDraftArgs {
	baseAttachments?: Api.IFileAttachment[];
	baseContent?: string;
	capabilities?: Api.ISocialMediaPlatformCapabilitiesResponse;
	customizations?: IPlatformCustomizationDraft[];
	customizeByPlatform?: boolean;
	targetedPlatforms: Api.SocialMediaType[];
}

/**
 * The API classifies an attachment by its MIME type, its stored file's content type, and its storage
 * resource type; only the first of those reaches this client, so an attachment still being transcoded
 * can read as neither image nor video here. The API calls it neither in that case too — it counts such
 * an attachment against the total ceiling alone — so both sides classify positively rather than
 * treating "not a video" as an image, and the two agree on what they can both see.
 */
const isVideo = (attachment: Api.IFileAttachment) => !!attachment.mimeType?.toLowerCase().startsWith('video/');

/** @see isVideo */
const isImage = (attachment: Api.IFileAttachment) => !!attachment.mimeType?.toLowerCase().startsWith('image/');

const count = (value: number, noun: string) => `${value.toLocaleString()} ${noun}${value === 1 ? '' : 's'}`;

/**
 * `minImages` is reported by the API and enforced by nothing, so nothing here checks it either — a
 * composer that blocked on it would refuse a post the save path takes. It is 1 on every platform.
 *
 * `maxMediaItems` is enforced, and ahead of the per-type ceilings: it is the only limit that binds an
 * attachment the API can classify as neither an image nor a video, so it is checked first here too.
 */
const validatePlatform = (
	platform: Api.SocialMediaType,
	content: string,
	attachments: Api.IFileAttachment[],
	capability: Api.ISocialMediaPlatformCapabilities | undefined,
	isSplit: boolean
): IPlatformValidationError[] => {
	const errors: IPlatformValidationError[] = [];
	const push = (field: PlatformValidationField, scope: PlatformValidationScope, message: string) =>
		errors.push({ platform, field, scope, message });

	if (isSplit && !content.trim()) {
		// Media does not stand in for words here. The API extracts each version's plain text and refuses
		// to publish one that comes back empty, the same rule the base post answers to, and it judges the
		// text alone whatever the version carries alongside it.
		push('content', 'publish', `Add text to the ${platform} version.`);
	}

	if (!capability) {
		return errors;
	}

	const videos = attachments.filter(isVideo);
	const images = attachments.filter(isImage);

	if (content.length > capability.maxContentCharacters) {
		push(
			'content',
			'save',
			isSplit
				? `${content.length.toLocaleString()} of ${capability.maxContentCharacters.toLocaleString()} characters. Shorten the ${platform} version.`
				: `${content.length.toLocaleString()} of ${capability.maxContentCharacters.toLocaleString()} characters. ${platform} allows ${capability.maxContentCharacters.toLocaleString()}.`
		);
	}

	if (capability.requiresMedia && !attachments.length) {
		push('attachments', 'publish', `${platform} posts need at least one image or video.`);
	}

	if (attachments.length > capability.maxMediaItems) {
		push(
			'attachments',
			'save',
			`${count(attachments.length, 'attachment')}. ${platform} allows ${capability.maxMediaItems}.`
		);
	}

	if (images.length > capability.maxImages) {
		push('attachments', 'save', `${count(images.length, 'image')}. ${platform} allows ${capability.maxImages}.`);
	}

	if (videos.length > capability.maxVideos) {
		push('attachments', 'save', `${count(videos.length, 'video')}. ${platform} allows ${capability.maxVideos}.`);
	}

	if (images.length && videos.length && !capability.allowsImagesWithVideo) {
		push('attachments', 'save', `${platform} can't post images and video in the same post.`);
	}

	return errors;
};

/**
 * The base post's media, held to the reduction across every targeted platform even while the post is
 * split. The save path checks the base against the combined limits whatever the split says, and the
 * base collects every upload made on any platform's tab, so what each tab adds counts toward one
 * post-wide limit. Crops and edits stay with their version and count toward nothing here. Reported
 * against the post rather than a platform, because no single version owns the problem.
 *
 * The combined media *requirement* is left out: the base keeps media while any version holds some, so
 * an empty base means empty versions, and the platform that requires media reports it by name.
 */
const validateSharedMediaPool = (
	attachments: Api.IFileAttachment[],
	combined: Api.ISocialMediaCombinedPlatformCapabilities | undefined
): IPlatformValidationError[] => {
	if (!combined || !attachments.length) {
		return [];
	}

	const errors: IPlatformValidationError[] = [];
	const push = (message: string) => errors.push({ field: 'attachments', scope: 'save', message });
	const videos = attachments.filter(isVideo);
	const images = attachments.filter(isImage);

	if (attachments.length > combined.maxMediaItems) {
		push(
			`${count(attachments.length, 'attachment')} added across all platforms. Only ${combined.maxMediaItems} can be added to one post.`
		);
	}

	if (images.length > combined.maxImages) {
		push(
			`${count(images.length, 'image')} added across all platforms. Only ${combined.maxImages} can be added to one post.`
		);
	}

	if (videos.length > combined.maxVideos) {
		push(
			`${count(videos.length, 'video')} added across all platforms. Only ${combined.maxVideos} can be added to one post.`
		);
	}

	if (images.length && videos.length && !combined.allowsImagesWithVideo) {
		push('Images and video can’t be added to the same post, even on different platforms.');
	}

	return errors;
};

/** The limit checks behind the composer's live per-tab feedback, run against the API's own figures. */
export const validateSocialPostDraft = ({
	baseAttachments,
	baseContent,
	capabilities,
	customizations,
	customizeByPlatform,
	targetedPlatforms,
}: IValidateSocialPostDraftArgs): IPlatformValidationError[] => {
	const perPlatform = targetedPlatforms.flatMap(platform => {
		const capability = findPlatformCapabilities(capabilities, platform);
		if (!customizeByPlatform) {
			return validatePlatform(platform, baseContent ?? '', baseAttachments ?? [], capability, false);
		}

		const customization = findPlatformCustomization(customizations, platform);
		if (!customization) {
			return [
				{
					platform,
					field: 'platform' as const,
					// The API gates this on the due date, so "turn customization on, then fill each
					// platform in" stays savable partway through.
					scope: 'publish' as const,
					message: `This post is customized by platform but has no ${platform} version.`,
				},
			];
		}
		return validatePlatform(platform, customization.content, customization.attachments, capability, true);
	});

	// Unsplit, the pool *is* every version, so the per-platform checks above already cover it — and
	// the reduction is by construction the set that satisfies all of them.
	if (!customizeByPlatform) {
		return perPlatform;
	}

	return [...validateSharedMediaPool(baseAttachments ?? [], capabilities?.combined), ...perPlatform];
};

export const getErrorsForPlatform = (errors: IPlatformValidationError[], platform: Api.SocialMediaType) =>
	errors.filter(error => error.platform === platform);

/** Problems with the post as a whole — shown alongside whichever version is on screen. */
export const getPostWideErrors = (errors: IPlatformValidationError[]) => errors.filter(error => !error.platform);

/** The errors the API would reject any save for, draft included. */
export const getSaveBlockingErrors = (errors: IPlatformValidationError[]) =>
	errors.filter(error => error.scope === 'save');
